import { useState, useEffect, useCallback } from 'react';
import { Building2, ChevronDown, Home, ExternalLink, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { superadminAPI } from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TenantIcon } from '@/components/TenantIcon';
import { toast } from 'sonner';
import { parseErrorDetail } from '@/lib/utils';

export function TenantSwitcher() {
  const { 
    tenant, 
    isSuperadmin, 
    viewingAsSuperadmin, 
    switchTenant, 
    returnHome,
    homeTenantId 
  } = useAuth();
  
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTenants = useCallback(async () => {
    try {
      const data = await superadminAPI.listTenants();
      setTenants(data.tenants || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error('Failed to load tenants', { description: parseErrorDetail(error).message });
    }
  }, []);

  useEffect(() => {
    if (!isSuperadmin) return undefined;

    loadTenants();
    window.addEventListener('eva:tenants-updated', loadTenants);
    return () => window.removeEventListener('eva:tenants-updated', loadTenants);
  }, [isSuperadmin, loadTenants]);

  const handleSwitchTenant = async (tenantId) => {
    if (tenantId === tenant?.tenant_id) return;
    
    setLoading(true);
    try {
      await switchTenant(tenantId);
      // Reload the page to refresh all data for the new tenant
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      toast.error('Failed to switch tenant', { description: parseErrorDetail(error).message });
    } finally {
      setLoading(false);
    }
  };

  const handleReturnHome = async () => {
    setLoading(true);
    try {
      await returnHome();
      window.location.reload();
    } catch (error) {
      console.error('Failed to return home:', error);
      toast.error('Failed to return to home tenant', { description: parseErrorDetail(error).message });
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperadmin) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 h-9 px-3"
          disabled={loading}
          data-testid="tenant-switcher-trigger"
        >
          <Building2 className="h-4 w-4" />
          <span className="max-w-[120px] truncate font-medium">
            {tenant?.display_name || 'Select Tenant'}
          </span>
          {viewingAsSuperadmin && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 bg-warning/10 text-warning dark:bg-warning/20 dark:text-warning">
              Viewing
            </Badge>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px]" data-testid="tenant-switcher-menu">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Switch Tenant
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {viewingAsSuperadmin && (
          <>
            <DropdownMenuItem 
              onClick={handleReturnHome}
              className="cursor-pointer"
              data-testid="return-home-btn"
            >
              <Home className="h-4 w-4 mr-2 text-info" />
              <span className="font-medium">Return to Home</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        <div className="max-h-[300px] overflow-y-auto">
          {tenants.map((t) => {
            const isCurrentTenant = t.id === tenant?.tenant_id;
            const isHomeTenant = t.id === homeTenantId;
            
            return (
              <DropdownMenuItem
                key={t.id}
                onClick={() => handleSwitchTenant(t.id)}
                className={`cursor-pointer ${isCurrentTenant ? 'bg-secondary' : ''}`}
                data-testid={`tenant-option-${t.shortname}`}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2 min-w-0">
                    <TenantIcon
                      name={t.display_name}
                      src={t.icon_url || t.logo_url}
                      color={t.primary_color}
                      className="w-6 h-6 rounded-md"
                    />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.display_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {t.email_domains}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    {isHomeTenant && (
                      <Home className="h-3 w-3 text-muted-foreground" />
                    )}
                    {isCurrentTenant && (
                      <Check className="h-4 w-4 text-success" />
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
          {tenants.filter((t) => t.id !== tenant?.tenant_id).length === 0 && (
            <div
              className="px-2 py-3 text-center text-xs text-muted-foreground"
              data-testid="tenant-switcher-empty"
            >
              No other tenants available to switch to.
            </div>
          )}
        </div>
        
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={() => window.location.href = '/superadmin/tenants'}
          className="cursor-pointer"
          data-testid="manage-tenants-btn"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          <span>Manage Tenants</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
